<template>
    <li
        class="question-card"
        :class="{ 'is-open': opened }"
        @focusin="$emit('activate')"
    >
        <div class="question-card__header">
            <span class="question-card__handle-icon" :title="$t('admin.quiz.questionsSection')">
                <fa icon="fa-solid fa-grip-vertical" />
            </span>
            <button
                type="button"
                class="question-card__heading"
                @click="$emit('toggle')"
            >
                <span class="question-card__title">
                    {{ $t('admin.quiz.questionTitle', { n: index + 1 }) }}
                </span>
                <p v-if="!opened" class="question-card__meta">
                    <span v-if="preview">{{ preview }}</span>
                    <span v-if="preview" class="question-card__meta-dot" aria-hidden="true">·</span>
                    <span>{{ alternativeLabel }}</span>
                    <template v-if="hasCorrect">
                        <span class="question-card__meta-dot" aria-hidden="true">·</span>
                        <span>{{ $t('admin.quiz.answerDefined') }}</span>
                    </template>
                </p>
            </button>
            <div class="question-card__actions">
                <button
                    type="button"
                    class="question-card__icon-btn"
                    :title="opened ? $t('admin.quiz.collapse') : $t('admin.quiz.expand')"
                    :aria-expanded="opened ? 'true' : 'false'"
                    @click="$emit('toggle')"
                >
                    <fa v-if="opened" icon="fa-solid fa-chevron-up" />
                    <fa v-else icon="fa-solid fa-chevron-down" />
                </button>
                <button
                    type="button"
                    class="question-card__icon-btn question-card__icon-btn--danger"
                    :title="$t('admin.quiz.removeQuestion')"
                    @click.stop="$emit('remove')"
                >
                    <fa icon="fa-solid fa-trash" />
                </button>
            </div>
        </div>

        <transition name="accordion">
            <div v-show="opened" class="question-card__body">
                <div class="quiz-fill-wrap">
                    <button
                        type="button"
                        class="quiz-fill"
                        @click="$emit('fill-sample')"
                    >
                        <fa icon="fa-solid fa-wand-magic-sparkles" />
                        <span>{{ $t('admin.quiz.fillQuestionData') }}</span>
                    </button>
                </div>
                <div class="question-card__block">
                    <label class="quiz-label">{{ $t('admin.quiz.statement') }}</label>
                    <div
                        class="custom-quill-wrapper"
                        :class="{ 'is-invalid': errors.description }"
                    >
                        <QuillEditor
                            v-model:content="question.description"
                            toolbar="essential"
                            content-type="html"
                            theme="snow"
                            class="custom-quill-editor"
                            :placeholder="$t('admin.quiz.statementPlaceholder')"
                        />
                    </div>
                    <p v-if="errors.description" class="quiz-field__error">
                        {{ errors.description }}
                    </p>
                </div>

                <div class="question-card__block">
                    <p class="quiz-label">{{ $t('admin.quiz.alternatives') }}</p>
                    <p class="question-card__hint">
                        {{ $t('admin.quiz.alternativesHint') }}
                    </p>
                    <ul class="quiz-alts">
                        <li
                            v-for="(answer, answerIndex) in question.answers || []"
                            :key="answer.id ? answer.id : `answer${answerIndex}OfQuestion${index}`"
                            class="quiz-alt-wrap"
                        >
                            <div
                                class="quiz-alt"
                                :class="{
                                    'is-correct': !!answer.correct,
                                    'is-invalid': errors.items && errors.items[answerIndex],
                                }"
                            >
                                <label class="quiz-alt__mark">
                                    <input
                                        type="radio"
                                        :title="$t('admin.quiz.markCorrect')"
                                        :name="'selectedAnswer' + index"
                                        :checked="!!answer.correct"
                                        @change="$emit('select-correct', answerIndex)"
                                    />
                                    <fa
                                        v-if="answer.correct"
                                        icon="fa-solid fa-check"
                                        aria-hidden="true"
                                    />
                                </label>
                                <span class="quiz-alt__letter" aria-hidden="true">
                                    {{ alternativeLetter(answerIndex) }}
                                </span>
                                <input
                                    type="text"
                                    class="quiz-alt__input"
                                    v-model="answer.description"
                                />
                                <button
                                    type="button"
                                    class="quiz-alt__delete"
                                    :title="$t('admin.quiz.removeAlternative')"
                                    :disabled="(question.answers || []).length <= 1"
                                    @click="$emit('remove-answer', answerIndex)"
                                >
                                    <fa icon="fa-solid fa-trash" />
                                </button>
                            </div>
                            <p
                                v-if="errors.items && errors.items[answerIndex]"
                                class="quiz-field__error"
                            >
                                {{ errors.items[answerIndex] }}
                            </p>
                        </li>
                    </ul>
                    <p v-if="errors.answers" class="quiz-field__error">
                        {{ errors.answers }}
                    </p>
                    <p v-if="errors.correct" class="quiz-field__error">
                        {{ errors.correct }}
                    </p>
                    <div v-show="canAddAnswer" class="quiz-alt-add-wrap">
                        <button
                            type="button"
                            class="quiz-alt-add"
                            @click="$emit('add-answer')"
                        >
                            <fa icon="fa-solid fa-circle-plus" />
                            <span>{{ $t('admin.quiz.addAlternative') }}</span>
                        </button>
                    </div>
                </div>

                <div class="question-card__block">
                    <label class="quiz-label" :for="`explanation${index}`">
                        {{ $t('admin.quiz.resolutionComment') }}
                    </label>
                    <p class="question-card__hint">
                        {{ $t('admin.quiz.resolutionCommentHint') }}
                    </p>
                    <div class="quiz-textarea">
                        <fa
                            class="quiz-textarea__icon"
                            icon="fa-solid fa-file-lines"
                            aria-hidden="true"
                        />
                        <textarea
                            :id="`explanation${index}`"
                            v-model="question.explanation"
                            class="quiz-textarea__control"
                            rows="3"
                            maxlength="1000"
                            :placeholder="$t('admin.quiz.resolutionCommentPlaceholder')"
                        ></textarea>
                    </div>
                </div>
            </div>
        </transition>
    </li>
</template>

<script>
    import { QuillEditor } from '@vueup/vue-quill';

    export default {
        name: 'QuizQuestionCard',
        components: {
            QuillEditor,
        },
        props: {
            question: {
                type: Object,
                required: true,
            },
            index: {
                type: Number,
                required: true,
            },
            opened: {
                type: Boolean,
                default: false,
            },
            canAddAnswer: {
                type: Boolean,
                default: true,
            },
            errors: {
                type: Object,
                default: () => ({
                    description: '',
                    answers: '',
                    correct: '',
                    items: {},
                }),
            },
        },
        emits: ['toggle', 'remove', 'activate', 'select-correct', 'add-answer', 'remove-answer', 'fill-sample'],
        computed: {
            preview() {
                const description = this.question?.description || '';
                const text = description.replace(/<[^>]+>/g, '').trim();
                if (!text) return '';
                return text.length > 60 ? `${text.slice(0, 60)}...` : text;
            },
            hasCorrect() {
                return (this.question.answers || []).some((answer) => answer.correct);
            },
            alternativeLabel() {
                const count = this.question.answers?.length || 0;
                if (count === 1) {
                    return this.$t('admin.quiz.statsAlternativeOne');
                }
                return this.$t('admin.quiz.statsAlternativeMany', { n: count });
            },
        },
        methods: {
            alternativeLetter(answerIndex) {
                return String.fromCharCode(65 + answerIndex);
            },
        },
    };
</script>
